import { ApiYAxisChartType } from "@incendium/api";
import {
  BarChart,
  Delete,
  Eject,
  ShowChart,
  StackedBarChart,
} from "@mui/icons-material";
import {
  Box,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import produce from "immer";
import { IChart } from "Interfaces";
import { useCallback, useMemo } from "react";
import { MetricSelector } from "features/analytics";

function ChartBuilderMetric({
  chart,
  setChart,
  index,
  yKey,
  noControls,
  noRemove,
}: {
  chart: IChart;
  setChart: React.Dispatch<React.SetStateAction<IChart>>;
  index: number;
  yKey: string;
  noControls?: boolean;
  noRemove?: boolean;
}) {
  const yAxisIdx: any = useMemo(() => {
    return chart.yAxisKeys.findIndex((y: any) => y.key === yKey);
  }, [chart, yKey]);

  // todo: type better
  const yAxis: any = useMemo(() => {
    if (yAxisIdx < 0) {
      return null;
    }

    return chart.yAxisKeys[yAxisIdx];
  }, [yAxisIdx, chart.yAxisKeys]);

  const lineType = useMemo(() => {
    if (!yAxis) {
      return ApiYAxisChartType.LINE;
    }

    if (
      yAxis?.stackId &&
      yAxis?.chart[yAxis.fields[index]] === ApiYAxisChartType.BAR
    ) {
      return yAxis?.chart[yAxis.fields[index]] + "-stacked";
    }
    return yAxis?.chart && yAxis?.chart[yAxis.fields[index]]
      ? yAxis?.chart[yAxis.fields[index]]
      : ApiYAxisChartType.LINE;
  }, [yAxis, index]);

  const filterFn = useCallback(
    (m) => !(yAxis.fields || []).includes(m.metric),
    [yAxis.fields]
  );

  const onRemove = useCallback(() => {
    setChart(
      produce(chart, (draft) => {
        if (yAxisIdx >= 0) {
          (draft.yAxisKeys[yAxisIdx] as any).fields.splice(index, 1);

          return;
        }
      })
    );
  }, [chart, index, setChart, yAxisIdx]);

  const onChange = useCallback(
    (e, v) => {
      setChart(
        produce(chart, (draft) => {
          if (yAxisIdx >= 0) {
            (draft.yAxisKeys[yAxisIdx] as any).fields[index] = v?.metric;
            return;
          }
          draft.yAxisKeys.push({
            key: "l",
            fields: [v?.metric || ""],
          });
        })
      );
    },
    [chart, index, setChart, yAxisIdx]
  );

  return (
    <Box sx={{ display: "flex", flex: 1, alignItems: "flex-start" }}>
      {noRemove || (
        <Box mr={2}>
          <IconButton size="small" onClick={onRemove}>
            <Delete />
          </IconButton>
        </Box>
      )}

      <MetricSelector
        metric={yAxis?.fields[index]}
        onChange={onChange}
        fullWidth
        filterFn={filterFn}
        hasControls={!noControls}
      />

      {noControls || (
        <Box ml={2}>
          <ToggleButtonGroup
            size="small"
            value={lineType}
            exclusive
            color="secondary"
            onChange={(e, v: string | null) => {
              if (!v) {
                return;
              }

              const value = (v || "").replace("-stacked", "");
              setChart((chart) =>
                produce(chart, (draft) => {
                  if (yAxisIdx >= 0) {
                    if (!(draft.yAxisKeys[yAxisIdx] as any).chart) {
                      (draft.yAxisKeys[yAxisIdx] as any).chart = {};
                    }
                  }
                  (draft.yAxisKeys[yAxisIdx] as any).chart[
                    yAxis.fields[index]
                  ] = value;
                  if ((v || "").includes("-stacked")) {
                    (draft.yAxisKeys[yAxisIdx] as any).stackId = yKey;
                  } else {
                    (draft.yAxisKeys[yAxisIdx] as any).stackId = undefined;
                  }
                })
              );
            }}
          >
            <ToggleButton value={ApiYAxisChartType.LINE}>
              <ShowChart />
            </ToggleButton>
            <ToggleButton value={ApiYAxisChartType.AREA}>
              <Eject />
            </ToggleButton>
            <ToggleButton value={ApiYAxisChartType.BAR}>
              <BarChart />
            </ToggleButton>
            <ToggleButton value={ApiYAxisChartType.BAR + "-stacked"}>
              <StackedBarChart />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}
    </Box>
  );
}

export default ChartBuilderMetric;
